// ---------------------------------
// client/src/components/SignaturePad.jsx
// Reusable signature capture widget backed by react-signature-canvas
// ---------------------------------
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const TEXT = {
    instructions: 'חתום בתוך התיבה בעזרת האצבע או עט.',
    clear: 'נקה',
    undo: 'בטל צעד',
    disabled: 'חתימה חסומה.',
};

const DEFAULT_HEIGHT = 180;

export default function SignaturePad({
    value,
    onChange,
    disabled = false,
    height = DEFAULT_HEIGHT,
    backgroundColor = 'transparent',
    penColor = '#1a202c',
}) {
    const canvasRef = useRef(null);
    const [isDirty, setIsDirty] = useState(Boolean(value));
    const lastValueRef = useRef(value || '');

    useEffect(() => {
        if (!canvasRef.current) {
            return;
        }
        if (!value) {
            canvasRef.current.clear();
            setIsDirty(false);
            lastValueRef.current = '';
            return;
        }
        if (value === lastValueRef.current) {
            return;
        }
        try {
            canvasRef.current.clear();
            canvasRef.current.fromDataURL(value);
            setIsDirty(true);
            lastValueRef.current = value;
        } catch (err) {
            console.warn('Failed loading signature data URL:', err);
            canvasRef.current.clear();
            setIsDirty(false);
            lastValueRef.current = '';
        }
    }, [value]);

    const emitChange = () => {
        if (!canvasRef.current) {
            return;
        }
        if (canvasRef.current.isEmpty()) {
            setIsDirty(false);
            lastValueRef.current = '';
            onChange?.('');
            return;
        }
        const trimmedCanvas = canvasRef.current.getTrimmedCanvas
            ? canvasRef.current.getTrimmedCanvas()
            : canvasRef.current.getCanvas();
        const dataUrl = trimmedCanvas.toDataURL('image/png');
        setIsDirty(true);
        lastValueRef.current = dataUrl;
        onChange?.(dataUrl);
    };

    const handleUndo = () => {
        if (!canvasRef.current) {
            return;
        }
        const data = canvasRef.current.toData();
        if (!Array.isArray(data) || data.length === 0) {
            return;
        }
        data.pop();
        canvasRef.current.fromData(data);
        emitChange();
    };

    const handleClear = () => {
        if (!canvasRef.current) {
            return;
        }
        canvasRef.current.clear();
        setIsDirty(false);
        lastValueRef.current = '';
        onChange?.('');
    };

    return (
        <div className="signature-pad">
            <div
                className={`signature-pad__canvas${disabled ? ' signature-pad__canvas--disabled' : ''}`}
                style={{ height }}
            >
                <SignatureCanvas
                    ref={canvasRef}
                    penColor={penColor}
                    canvasProps={{
                        className: 'signature-pad__surface',
                        style: { backgroundColor, height },
                    }}
                    clearOnResize={false}
                    onEnd={emitChange}
                    throttle={16}
                    minWidth={1.5}
                    maxWidth={2.5}
                    dotSize={2.5}
                    velocityFilterWeight={0.6}
                    backgroundColor={backgroundColor}
                    disabled={disabled}
                />
                {disabled && <div className="signature-pad__overlay">{TEXT.disabled}</div>}
            </div>
            {!disabled && (
                <div className="signature-pad__actions">
                    <span className="signature-pad__hint">{TEXT.instructions}</span>
                    <div className="signature-pad__buttons">
                        <button type="button" onClick={handleUndo} className="signature-pad__clear">
                            {TEXT.undo}
                        </button>
                        <button type="button" onClick={handleClear} className="signature-pad__clear">
                            {TEXT.clear}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
